/**
 * US address validation — pure client-side shape checks for the
 * Phase 2C MailCardSheet recipient + sender forms.
 *
 * No React, no Lob, zero imports. Mirrors the `card-date.ts` posture
 * from Phase 2B. Lob's `verifyAddress` fires server-side at the
 * checkout endpoint in Phase 3; this helper is the immediate-feedback
 * UX layer.
 *
 * Source of truth for the `UsAddressInput` shape. MailCardSheet +
 * MailCardRecipientForm + MailCardSenderForm + MailCardConfirmReview
 * all import the type from here (CLAUDE.md rule #4 sub-rule + plan
 * § 3 + § 5.3).
 *
 * Length caps measured in CODE POINTS via `[...value].length` (the
 * spread iterator counts supplementary-plane chars as one code point
 * each, matching the M.3.5.2 server-side sender_name normalization).
 *
 * Region classification is three-way (state / territory / invalid)
 * per plan § 5.2 + round-2 LOW 1 — territories (PR, GU, VI, AS, MP)
 * are recognized but excluded at v1; surfaced via a territory-specific
 * error key rather than lumping under generic "invalid state."
 */

export interface UsAddressInput {
  line1: string;
  line2: string; // empty string when absent
  city: string;
  region: string; // 2-letter US state code
  postalCode: string; // 5 digits or 5+4
}

export interface UsAddressValidationResult {
  ok: boolean;
  // Error string values are i18n KEYS (without the `mailCard.` prefix).
  // Callers resolve via `t('mailCard.' + key)`. Keeps the helper
  // i18n-free.
  errors: Partial<Record<keyof UsAddressInput | 'name', string>>;
}

export const US_STATES_ALLOWLIST: readonly string[] = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'DC',
] as const;

export const US_TERRITORY_CODES: readonly string[] = [
  'PR', 'GU', 'VI', 'AS', 'MP',
] as const;

export type RegionClassification =
  | { kind: 'state' }
  | { kind: 'territory'; code: string }
  | { kind: 'invalid' };

export function classifyUsRegion(input: string): RegionClassification {
  const normalized = input.trim().toUpperCase();
  if (US_STATES_ALLOWLIST.includes(normalized)) {
    return { kind: 'state' };
  }
  if (US_TERRITORY_CODES.includes(normalized)) {
    return { kind: 'territory', code: normalized };
  }
  return { kind: 'invalid' };
}

const POSTAL_REGEX = /^\d{5}(-\d{4})?$/;
const NAME_MAX_CODE_POINTS = 60;
const LINE_MAX_CODE_POINTS = 80;
const CITY_MAX_CODE_POINTS = 40;

function codePointLength(s: string): number {
  return [...s].length;
}

/**
 * True when the string contains any ASCII control char
 * (U+0000–U+001F or U+007F) OR a `<` / `>` glyph. Function-based
 * check avoids embedding literal control bytes in this file's
 * source (which a regex character class would require).
 */
function hasDisallowedNameChar(s: string): boolean {
  for (const ch of s) {
    const code = ch.codePointAt(0);
    if (code === undefined) continue;
    if (code <= 0x1f) return true;
    if (code === 0x7f) return true;
    if (code === 0x3c) return true; // '<'
    if (code === 0x3e) return true; // '>'
  }
  return false;
}

interface NameValidationKeys {
  required: string;
  tooLong: string;
  invalid: string;
}

interface AddressValidationKeys {
  line1Required: string;
  line1TooLong: string;
  line2TooLong: string;
  cityRequired: string;
  cityTooLong: string;
  regionInvalid: string;
  regionTerritory: string;
  postalInvalid: string;
}

function validateName(
  name: string,
  keys: NameValidationKeys,
): { ok: boolean; error?: string } {
  const trimmed = name.trim();
  if (trimmed === '') return { ok: false, error: keys.required };
  if (codePointLength(trimmed) > NAME_MAX_CODE_POINTS) {
    return { ok: false, error: keys.tooLong };
  }
  if (hasDisallowedNameChar(trimmed)) {
    return { ok: false, error: keys.invalid };
  }
  return { ok: true };
}

function validateAddress(
  address: UsAddressInput,
  keys: AddressValidationKeys,
): UsAddressValidationResult['errors'] {
  const errors: UsAddressValidationResult['errors'] = {};

  const line1Trimmed = address.line1.trim();
  if (line1Trimmed === '') {
    errors.line1 = keys.line1Required;
  } else if (codePointLength(line1Trimmed) > LINE_MAX_CODE_POINTS) {
    errors.line1 = keys.line1TooLong;
  }

  if (address.line2 !== '') {
    if (codePointLength(address.line2.trim()) > LINE_MAX_CODE_POINTS) {
      errors.line2 = keys.line2TooLong;
    }
  }

  const cityTrimmed = address.city.trim();
  if (cityTrimmed === '') {
    errors.city = keys.cityRequired;
  } else if (codePointLength(cityTrimmed) > CITY_MAX_CODE_POINTS) {
    errors.city = keys.cityTooLong;
  }

  const classification = classifyUsRegion(address.region);
  if (classification.kind === 'invalid') {
    errors.region = keys.regionInvalid;
  } else if (classification.kind === 'territory') {
    errors.region = keys.regionTerritory;
  }

  if (!POSTAL_REGEX.test(address.postalCode.trim())) {
    errors.postalCode = keys.postalInvalid;
  }

  return errors;
}

const RECIPIENT_NAME_KEYS: NameValidationKeys = {
  required: 'errorRecipientNameRequired',
  tooLong: 'errorRecipientNameTooLong',
  invalid: 'errorRecipientNameInvalid',
};

const SENDER_NAME_KEYS: NameValidationKeys = {
  required: 'errorSenderNameRequired',
  tooLong: 'errorSenderNameTooLong',
  invalid: 'errorSenderNameInvalid',
};

const RECIPIENT_ADDRESS_KEYS: AddressValidationKeys = {
  line1Required: 'errorRecipientLine1Required',
  line1TooLong: 'errorRecipientLine1TooLong',
  line2TooLong: 'errorRecipientLine2TooLong',
  cityRequired: 'errorRecipientCityRequired',
  cityTooLong: 'errorRecipientCityTooLong',
  regionInvalid: 'errorRecipientRegionInvalid',
  regionTerritory: 'errorRecipientRegionTerritory',
  postalInvalid: 'errorRecipientPostalInvalid',
};

const SENDER_ADDRESS_KEYS: AddressValidationKeys = {
  line1Required: 'errorSenderLine1Required',
  line1TooLong: 'errorSenderLine1TooLong',
  line2TooLong: 'errorSenderLine2TooLong',
  cityRequired: 'errorSenderCityRequired',
  cityTooLong: 'errorSenderCityTooLong',
  regionInvalid: 'errorSenderRegionInvalid',
  regionTerritory: 'errorSenderRegionTerritory',
  postalInvalid: 'errorSenderPostalInvalid',
};

export function validateRecipientForm(
  name: string,
  address: UsAddressInput,
): UsAddressValidationResult {
  const errors: UsAddressValidationResult['errors'] = {};
  const nameResult = validateName(name, RECIPIENT_NAME_KEYS);
  if (!nameResult.ok && nameResult.error) {
    errors.name = nameResult.error;
  }
  Object.assign(errors, validateAddress(address, RECIPIENT_ADDRESS_KEYS));
  return { ok: Object.keys(errors).length === 0, errors };
}

export function validateSenderForm(
  name: string,
  address: UsAddressInput,
): UsAddressValidationResult {
  const errors: UsAddressValidationResult['errors'] = {};
  const nameResult = validateName(name, SENDER_NAME_KEYS);
  if (!nameResult.ok && nameResult.error) {
    errors.name = nameResult.error;
  }
  Object.assign(errors, validateAddress(address, SENDER_ADDRESS_KEYS));
  return { ok: Object.keys(errors).length === 0, errors };
}
